import { useEffect, useState } from "react";

/**
 * Botón flotante de instalación PWA.
 * - Sólo aparece en navegadores que soportan beforeinstallprompt (Android/Chrome/Edge).
 * - Para iOS Safari mostramos instrucciones manuales después de 8s en mobile.
 * - Se oculta si ya está instalada (display-mode: standalone).
 */
export default function PwaInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);
  const [dismissed, setDismissed] = useState(
    typeof window !== "undefined" && localStorage.getItem("pwa_dismissed") === "1"
  );

  useEffect(() => {
    if (dismissed) return;

    // Already installed → never show
    const standalone = window.matchMedia("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
    if (standalone) return;

    // Android / Chrome / Edge
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShow(true), 5000); // 5s después de cargar
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari (no soporta beforeinstallprompt) → hint manual
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isIOS && isSafari) {
      setTimeout(() => setIosHint(true), 8000);
    }

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, [dismissed]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setShow(false);
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem("pwa_dismissed", "1");
    setDismissed(true);
    setShow(false);
    setIosHint(false);
  };

  if (dismissed) return null;

  // ===== Android / Chrome / Edge prompt =====
  if (show && deferredPrompt) {
    return (
      <div className="pwa-install-prompt" data-testid="pwa-install">
        <img src="/angel-64.png" alt="" className="pwa-icon" />
        <div className="pwa-text">
          <strong>Instala Mi Ángel en Casa</strong>
          <small>Acceso rápido desde tu pantalla de inicio</small>
        </div>
        <button className="pwa-btn-install" onClick={handleInstall} data-testid="pwa-install-btn">
          Instalar
        </button>
        <button className="pwa-btn-dismiss" onClick={handleDismiss} aria-label="Cerrar">
          ×
        </button>
      </div>
    );
  }

  // ===== iOS Safari hint (manual) =====
  if (iosHint) {
    return (
      <div className="pwa-install-prompt pwa-ios" data-testid="pwa-ios-hint">
        <img src="/angel-64.png" alt="" className="pwa-icon" />
        <div className="pwa-text">
          <strong>Agrega esta app a tu inicio</strong>
          <small>Toca <span style={{fontSize:18,verticalAlign:"middle"}}>⬆️</span> luego "Añadir a pantalla de inicio"</small>
        </div>
        <button className="pwa-btn-dismiss" onClick={handleDismiss} aria-label="Cerrar">
          ×
        </button>
      </div>
    );
  }

  return null;
}
